/**
 * @file        src/hooks/useTheme.js
 * @description Global Light/Dark theme controller for the booking frontend.
 *              Single source of truth for the active theme:
 *                • first visit  → follow the OS `prefers-color-scheme`
 *                • thereafter   → the user's saved choice (localStorage 'nh-theme')
 *              Applies the theme by setting `data-theme` on <html> (which re-points
 *              every CSS-variable token — see tailwind.config.js + global.css), so
 *              switching is instant and needs NO reload. A short-lived
 *              `.theme-transition` class cross-fades colours (~280ms) only during
 *              the swap. The no-FOUC bootstrap in index.html sets the initial
 *              attribute before React mounts; this hook keeps it in sync and lets
 *              any component read/toggle it. Purely presentational — no API/route/
 *              auth/business logic is touched.
 * @author      Kinetika Labs
 * @created     2026-07-15
 * @changelog
 *   - 2026-07-20 Claude — Now a SHARED store (useSyncExternalStore) instead of a
 *                          per-component useState. Three ThemeToggles mount at once
 *                          (Sidebar x2, AppLayout x1); with independent state, the
 *                          two that did not handle the click kept rendering the old
 *                          theme until something else re-rendered them, which read
 *                          as "some controls change late". They now all re-render
 *                          from one subscription.
 *                          Cross-fade shortened 280ms -> THEME_FADE_MS (200ms) and
 *                          exported so the CSS, the teardown timer and the toggle's
 *                          own animation share ONE duration.
 *   - 2026-07-15 Claude — Initial. Powers the header ThemeToggle.
 */
import { useCallback, useSyncExternalStore } from 'react';

const STORAGE_KEY = 'nh-theme';
const THEMES = ['light', 'dark'];

// ONE duration for the whole swap. global.css mirrors this in --nh-theme-fade and
// ThemeToggle mirrors it in its Tailwind duration-* classes. Previously the page
// faded over 280ms, the knob slid over 300ms and the sun/stars crossed over 500ms
// — three timings that finished at three different moments, which is what made the
// switch feel unsynchronised.
export const THEME_FADE_MS = 200;

/** The theme currently applied to <html> (falls back to the saved/OS choice). */
function currentTheme() {
  if (typeof document === 'undefined') return 'dark';
  const attr = document.documentElement.getAttribute('data-theme');
  if (THEMES.includes(attr)) return attr;
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (THEMES.includes(saved)) return saved;
  } catch { /* ignore */ }
  const light = typeof window !== 'undefined' && window.matchMedia
    && window.matchMedia('(prefers-color-scheme: light)').matches;
  return light ? 'light' : 'dark';
}

// Apply a theme to the document with a brief cross-fade. `persist=false` is used
// when the change comes from the OS (we only save what the USER explicitly picks,
// so a follower keeps following the system until they choose for themselves).
function applyTheme(theme, persist = true) {
  if (typeof document === 'undefined') return;
  const el = document.documentElement;
  el.classList.add('theme-transition');
  el.setAttribute('data-theme', theme);
  if (persist) {
    try { localStorage.setItem(STORAGE_KEY, theme); } catch { /* ignore */ }
  }
  // Drop the transition class after the swap so it never affects later hovers.
  // Small buffer past the fade so the last frame is not cut off mid-interpolation.
  window.clearTimeout(applyTheme._t);
  applyTheme._t = window.setTimeout(() => el.classList.remove('theme-transition'), THEME_FADE_MS + 60);
  emit();
}

// ---- shared store -------------------------------------------------------------
// A module-level subscriber set, so every useTheme() consumer re-renders off ONE
// source of truth. `data-theme` on <html> remains the actual state (the no-FOUC
// script in index.html sets it before React exists); this just notifies React.
const listeners = new Set();
function emit() { listeners.forEach((fn) => fn()); }
function subscribe(fn) { listeners.add(fn); return () => listeners.delete(fn); }
// getSnapshot must be referentially stable for identical state — it returns the
// 'light'/'dark' string, so React can bail out of re-rendering when unchanged.
function getSnapshot() { return currentTheme(); }
function getServerSnapshot() { return 'dark'; }

// OS listener is registered ONCE at module level rather than per-hook-instance:
// with three ThemeToggles mounted, the old per-component effect meant three
// listeners all reacting to the same OS change.
if (typeof window !== 'undefined' && window.matchMedia) {
  const mq = window.matchMedia('(prefers-color-scheme: light)');
  mq.addEventListener?.('change', (e) => {
    let saved = null;
    try { saved = localStorage.getItem(STORAGE_KEY); } catch { /* ignore */ }
    if (THEMES.includes(saved)) return; // user chose — don't override
    applyTheme(e.matches ? 'light' : 'dark', false);
  });
}

/**
 * @returns {{ theme: 'light'|'dark', isDark: boolean, toggle: () => void,
 *             setTheme: (t: 'light'|'dark') => void }}
 */
export default function useTheme() {
  const theme = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  const setTheme = useCallback((t) => {
    applyTheme(THEMES.includes(t) ? t : 'dark', true);
  }, []);

  const toggle = useCallback(() => {
    setTheme(currentTheme() === 'dark' ? 'light' : 'dark');
  }, [setTheme]);

  return { theme, isDark: theme === 'dark', toggle, setTheme };
}
